import React, { useMemo } from "react";
import { Box, Typography, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { Delete, StopRounded, FiberManualRecord } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * Gravação de voz no composer.
 * @param {boolean} embedded — dentro da cápsula do canal (substitui só o TextField); sem borda própria.
 */
const RecordingIndicator = ({ durationSeconds = 0, onStop, onDiscard, embedded = false }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const isCompact = useMediaQuery("(max-width:520px)");
  const barCount = isCompact ? 18 : 26;

  const bars = useMemo(
    () =>
      Array.from({ length: barCount }, (_, i) => {
        const phase = (Math.floor(durationSeconds * 3) + i * 2) % 11;
        const base = 6 + (phase % 5) * 3 + (i % 3) * 2;
        const height = Math.min(18, Math.max(5, base));
        const delay = `${((i * 37) % 500) / 1000}s`;
        return { i, height, delay };
      }),
    [barCount, durationSeconds]
  );

  const rootSx = embedded
    ? {
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 1 },
        flex: 1,
        alignSelf: "stretch",
        minWidth: 0,
        minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
        boxSizing: "border-box",
        py: 0,
        px: { xs: 0.25, sm: 0.5 },
        borderRadius: 0,
        border: "none",
        overflow: "hidden",
        width: "100%",
        boxShadow: "none",
        background: (th) =>
          th.palette.mode === "dark"
            ? alpha(th.palette.error.dark, 0.2)
            : alpha(th.palette.error.main, 0.07),
      }
    : {
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 1 },
        height: COMPOSER_ROW_HEIGHT_PX,
        minHeight: COMPOSER_ROW_HEIGHT_PX,
        maxHeight: COMPOSER_ROW_HEIGHT_PX,
        boxSizing: "border-box",
        py: 0,
        paddingInline: { xs: "4px 8px", sm: "8px 12px" },
        borderRadius: `${COMPOSER_ROW_HEIGHT_PX / 2}px`,
        overflow: "hidden",
        border: "1px solid",
        borderColor: (th) => alpha(th.palette.error.main, th.palette.mode === "dark" ? 0.45 : 0.35),
        background: (th) =>
          th.palette.mode === "dark"
            ? alpha(th.palette.error.dark, 0.18)
            : `linear-gradient(135deg, ${alpha(th.palette.common.white, 1)} 0%, ${alpha(
                th.palette.error.light,
                0.12
              )} 100%)`,
        width: "100%",
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 1px 0 ${alpha(theme.palette.common.black, 0.4)} inset`
            : "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 14px rgba(15, 23, 42, 0.06)",
      };

  return (
    <Box sx={rootSx}>
      <Tooltip title={t("input.discardRecordingAria")} enterDelay={300}>
        <IconButton
          onClick={onDiscard}
          size="small"
          sx={{
            color: "text.secondary",
            flexShrink: 0,
            padding: "8px",
            "&:hover": { color: "error.main", backgroundColor: "action.hover" },
          }}
          aria-label={t("input.discardRecordingAria")}
        >
          <Delete sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        <FiberManualRecord
          sx={{
            fontSize: 11,
            color: "error.main",
            flexShrink: 0,
            animation: "recordingBlink 1.2s ease-in-out infinite",
          }}
          aria-hidden
        />
        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            fontWeight: 700,
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            lineHeight: 1.2,
          }}
        >
          <Box
            component="span"
            sx={{
              color: "error.main",
              letterSpacing: 0.05,
              textTransform: "uppercase",
              fontSize: "0.68em",
              mr: 0.5,
            }}
          >
            {t("input.recordingBadge")}
          </Box>
          {formatTime(durationSeconds)}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.35,
          height: 24,
          minHeight: 24,
          maxHeight: 24,
          minWidth: 0,
          px: 0.25,
          overflow: "hidden",
        }}
        aria-hidden
      >
        {bars.map(({ i, height, delay }) => (
          <Box
            key={i}
            sx={{
              width: 3,
              height,
              borderRadius: 1.5,
              backgroundColor: (th) => alpha(th.palette.primary.main, 0.85),
              animation: "recordingPulse 0.55s ease-in-out infinite",
              animationDelay: delay,
              animationDirection: "alternate",
              transformOrigin: "center bottom",
            }}
          />
        ))}
      </Box>

      <Tooltip title={t("input.stopRecordingAria")} enterDelay={300}>
        <IconButton
          onClick={onStop}
          size="small"
          sx={{
            flexShrink: 0,
            padding: "8px",
            color: "primary.contrastText",
            backgroundColor: "primary.main",
            boxShadow: 1,
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
          aria-label={t("input.stopRecordingAria")}
        >
          <StopRounded sx={{ fontSize: 24 }} />
        </IconButton>
      </Tooltip>

      <style>{`
        @keyframes recordingPulse {
          0% { transform: scaleY(0.35); opacity: 0.75; }
          100% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes recordingBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
    </Box>
  );
};

export default RecordingIndicator;
